import {ID} from '@wandb/weave/core/util/id';
import React, {useRef} from 'react';
import {twMerge} from 'tailwind-merge';

export type TemplateSection = {
  rows: {
    groupTitle?: string;
    label: string;
    currDisplayValue: string | undefined;
    newDisplayValue?: string;
  }[];
};

export type WorkspaceTemplateDisplayValuesProps = {
  /**
   * templateSections contains groups of settings within that category
   */
  templateSections: TemplateSection[];
  /**
   * templateCategory represents the primary template classification (e.g. "Line plots")
   */
  templateCategory?: string;
  /**
   * Whether to show comparison between current and new values.
   * When false, only displays currDisplayValue without any strikethrough or new values.
   * @default true
   */
  showComparison?: boolean;
};

export const WorkspaceTemplateDisplayValues = ({
  templateSections,
  templateCategory,
  showComparison = true,
}: WorkspaceTemplateDisplayValuesProps) => {
  // Generate stable and unique IDs
  const ref = useRef({
    templateCategoryId: 'template-category-' + ID(),
  });

  // In display-only mode (settings page), return null if there are no explicitly saved values
  if (!showComparison) {
    const hasExplicitValues = templateSections.some(section =>
      section.rows.some(row => row.currDisplayValue != null)
    );
    if (!hasExplicitValues) {
      return null;
    }
  }

  return (
    <section
      className="mt-8 rounded bg-moon-300/[.24] p-16"
      // oxlint-disable-next-line react/refs
      aria-labelledby={ref.current.templateCategoryId}>
      {templateCategory && (
        <h5
          // oxlint-disable-next-line react/refs
          id={ref.current.templateCategoryId}
          className="mb-4 text-sm font-semibold uppercase text-wb-text-primary">
          {templateCategory}
        </h5>
      )}
      <div className="flex w-full flex-col gap-8 text-sm text-wb-text-primary">
        {/* oxlint-disable-next-line react/refs */}
        {templateSections.map((section, index) => {
          // Group rows by their groupTitle
          const groupedRows = section.rows.reduce<
            Record<string, typeof section.rows>
          >((acc, row) => {
            const groupKey = row.groupTitle ?? '';
            if (!acc[groupKey]) {
              acc[groupKey] = [];
            }
            acc[groupKey].push(row);
            return acc;
          }, {});
          const sectionKey = `${ref.current.templateCategoryId}-${index}`;
          return (
            <section key={sectionKey}>
              <dl role="list">
                {Object.entries(groupedRows).map(([groupTitle, rows]) => (
                  <div
                    key={groupTitle}
                    className="mb-8"
                    role="region"
                    aria-label={groupTitle || undefined}>
                    {groupTitle && (
                      <h6 className="mb-4 text-sm font-semibold text-wb-text-primary">
                        {groupTitle}
                      </h6>
                    )}
                    {rows
                      .filter(row =>
                        showComparison
                          ? // In comparison mode (drawer): show if either value exists
                            row.currDisplayValue != null ||
                            row.newDisplayValue != null
                          : // In display-only mode (settings page): only show explicitly saved values
                            row.currDisplayValue != null
                      )
                      .map((row, i) => {
                        const rowKey = `row-${i}-${groupTitle}`;
                        return (
                          <div key={rowKey} className="mb-4">
                            <DisplayRow
                              label={row.label}
                              currDisplayValue={row.currDisplayValue}
                              newDisplayValue={row.newDisplayValue}
                              showComparison={showComparison}
                            />
                          </div>
                        );
                      })}
                  </div>
                ))}
              </dl>
            </section>
          );
        })}
      </div>
    </section>
  );
};

const DisplayRow = ({
  label,
  currDisplayValue,
  newDisplayValue,
  showComparison,
}: {
  label: string;
  currDisplayValue: string | undefined;
  newDisplayValue: string | undefined;
  showComparison: boolean;
}) => {
  // Generate stable and unique IDs
  const ref = useRef({
    labelId: 'description-label-' + ID(),
  });
  const isValueModified =
    showComparison &&
    newDisplayValue != null &&
    newDisplayValue !== currDisplayValue;

  return (
    <div className="flex text-wb-text-tertiary" role="listitem">
      {/* oxlint-disable-next-line react/refs */}
      <dt className="w-1/3" id={ref.current.labelId}>
        {label}
      </dt>
      <span className="w-16">:</span>
      {/* oxlint-disable-next-line react/refs */}
      <dd className="m-0" aria-labelledby={ref.current.labelId}>
        {currDisplayValue && (
          <span
            className={twMerge(isValueModified && 'line-through')}
            aria-label="Existing template value">
            {currDisplayValue}
          </span>
        )}
        {isValueModified && (
          <span
            className={twMerge(currDisplayValue != null && 'ml-4')}
            aria-label="Updated template value">
            {newDisplayValue}
          </span>
        )}
      </dd>
    </div>
  );
};
